import React, { FC, memo, useEffect, useRef, useState } from 'react';
import AddEditLocationParameter from './index';
import { get } from 'src/services/GenesisApi';
import { getParameterDefinitionsUrl, ParameterScope } from 'src/constants/EUMarkets';
import { handleError } from 'src/utils/common/utils';
import { INGParameters, IParameterDefinition } from 'src/types/EUMarkets.type';

interface IInlineParameterListProps {
  open: boolean;
  scopeId: ParameterScope;
  locationId: number;
  parameters: INGParameters[];
  isAdding: boolean;
  addingParameterCount?: number;
  pendingParameterIds?: string[];
  onDraftChange: (parameter: INGParameters, rowIndex: number, error?: string) => void;
  onRemove: (parameterIndex: number) => void;
  onCancelAdd: (parameterIndex?: number) => void;
  onDefinitionsLoaded?: (definitions: IParameterDefinition[]) => void;
}

export const getParameterKey = (parameter: INGParameters) =>
  [
    parameter.id,
    parameter.parameterDefinitionId,
    parameter.valueString,
    parameter.valueLong,
    parameter.valueDouble,
    parameter.valueBoolean,
    parameter.validFrom,
    parameter.validTo,
  ].join('|');

const InlineParameterList: FC<IInlineParameterListProps> = memo(
  ({
    open,
    scopeId,
    locationId,
    parameters,
    isAdding,
    addingParameterCount = isAdding ? 1 : 0,
    pendingParameterIds,
    onDraftChange,
    onRemove,
    onCancelAdd,
    onDefinitionsLoaded,
  }) => {
    const [definitions, setDefinitions] = useState<IParameterDefinition[]>([]);
    const [loadingDefinitions, setLoadingDefinitions] = useState(false);
    const onDefinitionsLoadedRef = useRef(onDefinitionsLoaded);
    onDefinitionsLoadedRef.current = onDefinitionsLoaded;
    const pendingKeyIdRef = useRef(0);
    const pendingKeysRef = useRef<string[]>([]);
    while (pendingKeysRef.current.length < addingParameterCount) {
      pendingKeysRef.current.push(`pending-parameter-${pendingKeyIdRef.current}`);

      pendingKeyIdRef.current += 1;
    }

    if (pendingKeysRef.current.length > addingParameterCount) {
      pendingKeysRef.current = pendingKeysRef.current.slice(0, addingParameterCount);
    }
    const uniqueParameters = parameters.reduce<Array<{ parameter: INGParameters; index: number }>>(
      (items, parameter, index) =>
        items.some((item) => getParameterKey(item.parameter) === getParameterKey(parameter))
          ? items
          : [...items, { parameter, index }],
      [],
    );

    useEffect(() => {
      if (!open) {
        return;
      }
      setLoadingDefinitions(true);
      get(getParameterDefinitionsUrl(scopeId))
        .then((response) => {
          const loadedDefinitions: IParameterDefinition[] = response.data || [];
          setDefinitions(loadedDefinitions);
          onDefinitionsLoadedRef.current?.(loadedDefinitions);
        })
        .catch((error) => handleError(error, 'Unable to load parameter definitions.'))
        .finally(() => setLoadingDefinitions(false));
    }, [open, scopeId]);

    return (
      <div className='parameter-inline-list'>
        {uniqueParameters.map(({ parameter, index: parameterIndex }) => (
          <AddEditLocationParameter
            key={`${parameter.id}-${parameterIndex}`}
            open={open}
            inline
            scopeId={scopeId}
            locationId={locationId}
            parameter={parameter}
            definitions={definitions}
            loadingDefinitions={loadingDefinitions}
            onClose={() => onRemove(parameterIndex)}
            onDraftChange={(draft, error) => {
              if (draft === null) {
                return;
              }
              onDraftChange(draft, parameterIndex, error);
            }}
          />
        ))}
        {Array.from({ length: addingParameterCount }, (_, pendingIndex) => {
          const rowIndex = parameters.length + pendingIndex;
          return (
            <AddEditLocationParameter
              key={pendingParameterIds?.[pendingIndex] ?? pendingKeysRef.current[pendingIndex]}
              open={open}
              inline
              scopeId={scopeId}
              locationId={locationId}
              parameter={null}
              definitions={definitions}
              loadingDefinitions={loadingDefinitions}
              onClose={() => {
                if (!pendingParameterIds) {
                  pendingKeysRef.current.splice(pendingIndex, 1);
                }
                onCancelAdd(rowIndex);
              }}
              onDraftChange={(draft, error) => draft && onDraftChange(draft, rowIndex, error)}
            />
          );
        })}
      </div>
    );
  },
);

export default InlineParameterList;
